import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'common/components/Form/Button';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import RowMenu from 'modules/settings/components/DataExport/RowMenu';
import ConfirmationModal from 'common/components/Modal/ConfirmationModal';
import { Text } from 'common/styledComponents/typography';
import { useShowToast } from 'common/hooks/notification';
import {
  useCreateDataExportWebhook,
  useDataExportTokens,
  useDataExportWebhooks,
  useForgetDataExportCredentials,
  useRemoveDataExportWebhook,
  useResumeDataExportWebhook,
  useSendDataExportWebhookTest,
} from 'common/queries/dataExport';
import { CreateDataExportWebhookPayload, DataExportWebhook, DataExportWebhookStatus } from 'common/types/dataExport';
import AddWebhookModal from 'modules/settings/components/DataExport/AddWebhookModal';
import WebhooksGuide from 'modules/settings/components/DataExport/WebhooksGuide';
import WebhookDeliveries from 'modules/settings/components/DataExport/WebhookDeliveries';
import OnceOnlySecretModal from 'modules/settings/components/DataExport/OnceOnlySecretModal';
import { SectionEmpty, SectionError, SectionLoading } from 'modules/settings/components/DataExport/SectionStatus';
import { errorToast, formatExportDate, formatExportDateTime, formatExportTime } from 'modules/settings/components/DataExport/utils';
import {
  BadgeTone,
  Cell,
  ColumnLabel,
  Description,
  GuideRow,
  Hint,
  List,
  MutedLine,
  Row,
  RowActions,
  Section,
  SectionHeader,
  SectionTitle,
  ToneBadge,
  WebhookUrl,
} from 'modules/settings/components/DataExport/styled';

const COLUMNS = '3fr 1.1fr 1.4fr 1.6fr auto';
const STATUS_TONES: Record<DataExportWebhookStatus, BadgeTone> = { active: 'green', failing: 'orange', paused: 'red' };

const WebhooksSection: React.FC = () => {
  const { t } = useTranslation();
  const showToast = useShowToast();
  const { data, isLoading, isError, error, refetch } = useDataExportWebhooks();
  // Owned here rather than by the form modal, which unmounts as soon as the webhook is registered.
  const createWebhook = useCreateDataExportWebhook();
  const forgetCredentials = useForgetDataExportCredentials();
  const removeWebhook = useRemoveDataExportWebhook();
  const sendTest = useSendDataExportWebhookTest();
  const resumeWebhook = useResumeDataExportWebhook();
  // Already loaded by the tokens section; the event feed example in the guide uses its URL.
  const downloadApiUrl = useDataExportTokens().data?.download_api_url;
  const [expandedId, setExpandedId] = React.useState<number | null>(null);
  const [isAddOpen, setIsAddOpen] = React.useState(false);
  const [generatedSecret, setGeneratedSecret] = React.useState<string | null>(null);
  const [webhookToRemove, setWebhookToRemove] = React.useState<DataExportWebhook | null>(null);

  const webhooks = data?.webhook_subscriptions ?? [];
  const maxSubscriptions = data?.max_subscriptions ?? 0;
  const isAtLimit = !!data && webhooks.length >= maxSubscriptions;

  const handleCreate = async (payload: CreateDataExportWebhookPayload) => {
    const { secret } = await createWebhook.mutateAsync(payload);
    setIsAddOpen(false);
    // A supplied secret (in the variables) or a generated one (in the response): keep neither in React Query.
    createWebhook.reset();
    forgetCredentials();
    if (secret) setGeneratedSecret(secret);
    else showToast({ status: 'success', message: t('dataExport.webhooks.added') });
  };

  const handleAddClose = () => {
    setIsAddOpen(false);
    createWebhook.reset();
    forgetCredentials();
  };

  const handleRemove = () => {
    if (!webhookToRemove) return;

    removeWebhook.mutate(webhookToRemove.id, {
      onSuccess: () => showToast({ status: 'success', message: t('dataExport.webhooks.removed') }),
      onError: e => showToast(errorToast(e, t('dataExport.webhooks.removeFailed'))),
      onSettled: () => setWebhookToRemove(null),
    });
  };

  const handleSendTest = (webhook: DataExportWebhook) => {
    sendTest.mutate(webhook.id, {
      onSuccess: () => {
        setExpandedId(webhook.id);
        showToast({ status: 'success', message: t('dataExport.webhooks.testSent') });
      },
      onError: e => showToast(errorToast(e, t('dataExport.webhooks.testFailed'))),
    });
  };

  const handleResume = (webhook: DataExportWebhook) => {
    resumeWebhook.mutate(webhook.id, {
      onSuccess: () => showToast({ status: 'success', message: t('dataExport.webhooks.resumed') }),
      onError: e => showToast(errorToast(e, t('dataExport.webhooks.resumeFailed'))),
    });
  };

  /** The line under the URL that says since when a webhook is failing or paused. */
  const statusDetail = (webhook: DataExportWebhook) => {
    if (webhook.status === 'paused' && webhook.paused_at) {
      return t('dataExport.webhooks.status.pausedSince', { date: formatExportDateTime(webhook.paused_at) });
    }
    if (webhook.status === 'failing' && webhook.failing_since) {
      return t('dataExport.webhooks.status.failingSince', { date: formatExportDateTime(webhook.failing_since) });
    }
    return null;
  };

  const renderList = () => {
    if (isLoading) return <SectionLoading />;
    if (isError) return <SectionError error={error} onRetry={() => refetch()} />;
    if (!webhooks.length) {
      return (
        <SectionEmpty
          icon='lightning'
          title={t('dataExport.webhooks.emptyTitle')}
          message={t('dataExport.webhooks.empty')}
          action={{ label: t('dataExport.webhooks.addFirst'), onClick: () => setIsAddOpen(true), disabled: !data || isAtLimit }}
        />
      );
    }

    return (
      <List data-testid='data-export-webhooks'>
        <Row columns={COLUMNS} data-header='true' aria-hidden='true'>
          {['url', 'status', 'created', 'lastDelivery'].map(column => (
            <Cell key={column}>
              <Text variant='body2' fontWeight='600'>
                {t(`dataExport.webhooks.columns.${column}`)}
              </Text>
            </Cell>
          ))}
          <Cell />
        </Row>
        {webhooks.map(webhook => (
          <React.Fragment key={webhook.id}>
            <Row columns={COLUMNS} data-testid='data-export-webhook-row'>
              <Cell>
                <ColumnLabel>{`${t('dataExport.webhooks.columns.url')}: `}</ColumnLabel>
                <WebhookUrl>{webhook.url}</WebhookUrl>
                {statusDetail(webhook) && <MutedLine data-tone={STATUS_TONES[webhook.status]}>{statusDetail(webhook)}</MutedLine>}
              </Cell>
              <Cell>
                <ColumnLabel>{`${t('dataExport.webhooks.columns.status')}: `}</ColumnLabel>
                <ToneBadge tone={STATUS_TONES[webhook.status]} data-testid='data-export-webhook-status'>
                  {t(`dataExport.webhooks.status.${webhook.status}`)}
                </ToneBadge>
              </Cell>
              <Cell>
                <ColumnLabel>{`${t('dataExport.webhooks.columns.created')}: `}</ColumnLabel>
                <Text variant='body2'>{formatExportDate(webhook.created_at)}</Text>
                {webhook.created_by && <MutedLine>{t('dataExport.by', { name: webhook.created_by })}</MutedLine>}
              </Cell>
              <Cell>
                <ColumnLabel>{`${t('dataExport.webhooks.columns.lastDelivery')}: `}</ColumnLabel>
                {webhook.last_delivery_at ? (
                  <>
                    <Text variant='body2'>{formatExportDate(webhook.last_delivery_at)}</Text>
                    <MutedLine>
                      {[formatExportTime(webhook.last_delivery_at), webhook.last_delivery_status].filter(Boolean).join(' · ')}
                    </MutedLine>
                  </>
                ) : (
                  <Text variant='body2' color='black50'>
                    {t('dataExport.never')}
                  </Text>
                )}
              </Cell>
              <RowActions>
                {webhook.status === 'paused' ? (
                  <Button
                    type='button'
                    color='lightPurple'
                    pill={false}
                    isButtonV2
                    disabled={resumeWebhook.isLoading}
                    onClick={() => handleResume(webhook)}
                  >
                    {t('dataExport.webhooks.resume')}
                  </Button>
                ) : (
                  <SecondaryButton
                    type='button'
                    color='white'
                    compact
                    isButtonV2
                    disabled={sendTest.isLoading}
                    onClick={() => handleSendTest(webhook)}
                  >
                    {t('dataExport.webhooks.sendTest')}
                  </SecondaryButton>
                )}
                <SecondaryButton
                  type='button'
                  color='white'
                  compact
                  isButtonV2
                  aria-expanded={expandedId === webhook.id}
                  aria-controls={`data-export-deliveries-${webhook.id}`}
                  onClick={() => setExpandedId(expandedId === webhook.id ? null : webhook.id)}
                >
                  {t(expandedId === webhook.id ? 'dataExport.webhooks.hideDeliveries' : 'dataExport.webhooks.showDeliveries')}
                </SecondaryButton>
                <RowMenu
                  label={t('dataExport.menu')}
                  items={[{ label: t('dataExport.webhooks.remove'), onSelect: () => setWebhookToRemove(webhook) }]}
                />
              </RowActions>
            </Row>
            {expandedId === webhook.id && <WebhookDeliveries webhook={webhook} retentionDays={data?.retention_days} />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Section aria-labelledby='data-export-webhooks-title'>
      <SectionHeader>
        <SectionTitle id='data-export-webhooks-title'>{t('dataExport.webhooks.title')}</SectionTitle>
        <Button type='button' color='lightPurple' pill={false} isButtonV2 disabled={!data || isAtLimit} onClick={() => setIsAddOpen(true)}>
          {t('dataExport.webhooks.add')}
        </Button>
      </SectionHeader>
      <Description>
        <Text variant='body2' color='black75'>
          {t('dataExport.webhooks.description')}
        </Text>
        {data && (
          <Text variant='body2' color='black75'>
            {t('dataExport.webhooks.limit', { count: maxSubscriptions })}
          </Text>
        )}
      </Description>
      <GuideRow>
        <WebhooksGuide maxSubscriptions={data?.max_subscriptions} downloadApiUrl={downloadApiUrl} />
      </GuideRow>
      {isAtLimit && (
        <Hint>
          <Text variant='body2' color='wine'>
            {t('dataExport.webhooks.limitReached', { count: maxSubscriptions })}
          </Text>
        </Hint>
      )}
      {renderList()}
      {isAddOpen && <AddWebhookModal isSubmitting={createWebhook.isLoading} onSubmit={handleCreate} onClose={handleAddClose} />}
      <ConfirmationModal
        appearance='card'
        id='data-export-remove-webhook-modal'
        open={!!webhookToRemove}
        isLoading={removeWebhook.isLoading}
        title={t('dataExport.webhooks.removeModal.title')}
        confirmButtonTitle={t('dataExport.webhooks.removeModal.confirm')}
        onCancel={() => setWebhookToRemove(null)}
        onConfirm={handleRemove}
      >
        <Text variant='body2' color='black'>
          {t('dataExport.webhooks.removeModal.body', { url: webhookToRemove?.url })}
        </Text>
      </ConfirmationModal>
      {generatedSecret && (
        <OnceOnlySecretModal
          modalId='data-export-webhook-secret-modal'
          title={t('dataExport.webhooks.secretModal.title')}
          value={generatedSecret}
          warning={t('dataExport.webhooks.secretModal.warning')}
          acknowledgeLabel={t('dataExport.webhooks.secretModal.acknowledge')}
          onClose={() => setGeneratedSecret(null)}
        />
      )}
    </Section>
  );
};

export default WebhooksSection;
